import { useState, useEffect, useCallback } from 'react';
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2, Building2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const MAX_DISPLAY_ROWS = 50;

function fmt(n) {
  if (n === null || n === undefined) return '-';
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function AlEciReclassificationPanel() {
  const [ruledEntities, setRuledEntities] = useState([]);
  const [entityId, setEntityId] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [applying, setApplying] = useState(false);
  const [confirmApply, setConfirmApply] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    diagnosticsAPI.propertyReclassificationRuledEntities()
      .then(({ data }) => {
        setRuledEntities(data.entities || []);
        if (data.entities?.length && !entityId) setEntityId(data.entities[0].id);
      })
      .catch((err) => toast.error(getErrorMessage(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runPreview = async () => {
    if (!entityId) return;
    setLoadingPreview(true);
    setError(null);
    try {
      const { data } = await diagnosticsAPI.propertyReclassificationPreview(entityId);
      setPreview(data);
    } catch (err) {
      setError(getErrorMessage(err));
      toast.error(getErrorMessage(err));
    } finally {
      setLoadingPreview(false);
    }
  };

  const runApply = async () => {
    if (!entityId) return;
    setApplying(true);
    setConfirmApply(false);
    setError(null);
    try {
      const { data } = await diagnosticsAPI.propertyReclassificationApply(entityId);
      setApplyResult(data);
      if (data.properties_flipped > 0) {
        toast.success(`Flipped ${data.properties_flipped} property(ies) to managed; restamped ${data.restamp_summary?.total?.restamped ?? 0} booking(s).`);
      } else {
        toast.info('Nothing to flip -- no candidate properties found.');
      }
      // Refresh preview so the card reflects the new state immediately.
      runPreview();
    } catch (err) {
      setError(getErrorMessage(err));
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  const onEntityChange = (value) => {
    setEntityId(value);
    setPreview(null);
    setApplyResult(null);
    setError(null);
    setConfirmApply(false);
  };

  const currency = preview?.money_movement?.currency || 'GBP';
  const entityName = ruledEntities.find((e) => e.id === entityId)?.name || entityId;

  return (
    <Card data-testid="al-eci-reclassification-panel">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Building2 className="h-5 w-5 text-blue-500" />
          Property Reclassification (never self-operated)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Domain ruling: entities in this list never self-operate -- every property is
          externally managed, whether or not a management statement has been ingested for
          it yet. Their owned/direct-receivable population is a property-level
          management_type misclassification, invisible to both the restamp census (starts
          from managed properties) and the contradiction census (starts from statements) by
          construction. Preview is read-only. Apply flips candidate properties to managed
          and runs the existing booking-level restamp so the receivable total drops
          immediately; properties a human has explicitly set to &quot;owned&quot; are never
          touched -- listed as exceptions instead. Adding an entity to this list requires a
          new Fan ruling, not an operator choice.
        </p>

        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={entityId}
            onValueChange={onEntityChange}
            disabled={loadingPreview || applying}
          >
            <SelectTrigger className="h-9 w-auto min-w-[200px] text-sm" data-testid="reclass-entity-select">
              <SelectValue placeholder="Select entity..." />
            </SelectTrigger>
            <SelectContent>
              {ruledEntities.map((e) => (
                <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={runPreview} disabled={loadingPreview || !entityId} variant="outline" data-testid="eci-reclass-preview-btn">
            {loadingPreview ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Preview
          </Button>
          {!confirmApply ? (
            <Button
              variant="destructive"
              onClick={() => setConfirmApply(true)}
              disabled={applying || !entityId}
              data-testid="eci-reclass-apply-btn"
            >
              Apply
            </Button>
          ) : (
            <Button variant="destructive" onClick={runApply} disabled={applying} data-testid="eci-reclass-confirm-apply-btn">
              {applying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <AlertTriangle className="h-4 w-4 mr-2" />}
              Confirm Apply for {entityName}
            </Button>
          )}
        </div>

        {error && (
          <div className="text-sm text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg p-3">
            Error: {error}
          </div>
        )}

        {preview && (
          <div className="space-y-3" data-testid="eci-reclass-preview-results">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Candidates</p>
                <p className="text-xl font-bold tabular-nums">{preview.candidates.length}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Exceptions (operator-set)</p>
                <p className="text-xl font-bold tabular-nums text-amber-600 dark:text-amber-400">{preview.exceptions.length}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Direct receivable: before</p>
                <p className="text-lg font-bold tabular-nums">{fmt(preview.money_movement.direct_receivable_before_native)} {currency}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Direct receivable: after</p>
                <p className="text-lg font-bold tabular-nums text-green-600 dark:text-green-400">{fmt(preview.money_movement.direct_receivable_after_native)} {currency}</p>
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              Moves to Owner-collected / &quot;Managed bookings awaiting statement&quot; pipeline:{' '}
              <span className="font-semibold tabular-nums">{fmt(preview.money_movement.moves_to_owner_collected_pipeline_native)} {currency}</span>.
              Existing management statements for {preview.entity_name} (unaffected by this operation): {preview.money_movement.existing_statement_count}.
            </p>

            {preview.candidates.length === 0 && preview.exceptions.length === 0 ? (
              <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                <CheckCircle2 className="h-4 w-4" />
                No {preview.entity_name} properties currently stamped owned.
              </div>
            ) : (
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-3 py-2 font-medium">Property</th>
                      <th className="text-left px-3 py-2 font-medium">Platform</th>
                      <th className="text-right px-3 py-2 font-medium">Bookings</th>
                      <th className="text-left px-3 py-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...preview.candidates.map((r) => ({ ...r, _kind: 'candidate' })),
                      ...preview.exceptions.map((r) => ({ ...r, _kind: 'exception' }))]
                      .slice(0, MAX_DISPLAY_ROWS)
                      .map((r, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="px-3 py-2">{r.name}</td>
                          <td className="px-3 py-2"><Badge variant="outline" className="text-[10px]">{r.platform}</Badge></td>
                          <td className="px-3 py-2 text-right tabular-nums">{r.booking_count}</td>
                          <td className="px-3 py-2">
                            {r._kind === 'candidate' ? (
                              <Badge className="text-[10px] bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                                will flip to managed
                              </Badge>
                            ) : (
                              <Badge className="text-[10px] bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                                exception: operator-set owned
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {applyResult && (
          <div className="space-y-1" data-testid="eci-reclass-apply-results">
            <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
              <CheckCircle2 className="h-4 w-4" />
              Flipped {applyResult.properties_flipped} of {applyResult.properties_candidates_seen} candidate(s), skipped{' '}
              {applyResult.exceptions_skipped} exception(s). Restamped {applyResult.restamp_summary?.total?.restamped ?? 0} booking(s).
              Direct receivable now {fmt(applyResult.after?.direct_receivable_total_native)} {currency}.
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
